import { useRef, useState, useEffect, useCallback } from 'react';
import {
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import DatePicker from 'react-multi-date-picker';
import DatePanel from 'react-multi-date-picker/plugins/date_panel';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { filterUndefined } from '../../utils/FilterUndefinedObject';
import './MultipleDateSelector.scss';

export const DATE_SELECTOR_MODE = {
  DATE_RANGE: '0',
  MULTIPLE_DATES: '1',
};

const getMultipleDateSelectorPlaceHolder = (mode, showText, openCalendar) => (
  // eslint-disable-next-line
  <div className='dates-selector' onClick={openCalendar} tabIndex={0} role='button' onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { openCalendar(); } }} aria-label='Select Dates'>
    <DateRangeIcon className='icon' />
    {showText && mode === DATE_SELECTOR_MODE.DATE_RANGE && 'CLICK TO SELECT DATE RANGE'}
    {showText && mode === DATE_SELECTOR_MODE.MULTIPLE_DATES && 'CLICK TO SELECT DAYS'}
  </div>
);

const defaultProps = {
  selectedDates: {},
  onSelectedDatesChange: () => null,
  onClose: () => null,
  onApply: () => null,
  selectorMode: null,
  minDate: null,
  maxDate: null,
  noApplyButton: false,
  noClearButton: false,
  className: '',
  showDatePanel: false,
  formMode: false,
  highlightToday: true,
  showText: true,
};

export default function MultipleDateSelector(props) {
  const {
    selectedDates,
    onSelectedDatesChange,
    onClose,
    onApply,
    selectorMode,
    minDate,
    maxDate,
    noApplyButton,
    noClearButton,
    className,
    showDatePanel,
    formMode,
    highlightToday,
    showText,
  } = { ...defaultProps, ...filterUndefined(props) };
  const datePickerRef = useRef(null);
  const overlayFocusTrapCleanupRef = useRef(null);
  const [mode, setMode] = useState(() => {
    if (selectorMode !== null) {
      return selectorMode;
    }
    if (!selectedDates || selectedDates.isDateRange === null || selectedDates.isDateRange === undefined) {
      return DATE_SELECTOR_MODE.DATE_RANGE;
    }
    return selectedDates.isDateRange ? DATE_SELECTOR_MODE.DATE_RANGE : DATE_SELECTOR_MODE.MULTIPLE_DATES;
  });

  const clearOverlayFocusTrap = useCallback(() => {
    if (overlayFocusTrapCleanupRef.current) {
      overlayFocusTrapCleanupRef.current();
      overlayFocusTrapCleanupRef.current = null;
    }
  }, []);

  const closeCalendarOverlay = useCallback(() => {
    if (datePickerRef.current && typeof datePickerRef.current.closeCalendar === 'function') {
      datePickerRef.current.closeCalendar();
    }
  }, []);

  const isVisible = (element) => Boolean(
    element
    && (element.offsetWidth || element.offsetHeight || element.getClientRects().length),
  );

  const overlayFocusableSelector = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[role="button"]',
  ].join(', ');

  const getActiveOverlay = () => (
    Array.from(document.querySelectorAll('.date-picker-component .rmdp-wrapper, .date-picker-component .rmdp-calendar'))
      .find((overlayElement) => isVisible(overlayElement))
  );

  const getFocusableElements = (overlayElement) => (
    Array.from(overlayElement.querySelectorAll(overlayFocusableSelector))
      .filter((element) => isVisible(element) && element.getAttribute('aria-hidden') !== 'true')
  );

  const getFirstVisiblePickerElement = (overlayElement) => {
    const yearPicker = overlayElement.querySelector('.rmdp-year-picker');
    const monthPicker = overlayElement.querySelector('.rmdp-month-picker');
    const dayPicker = overlayElement.querySelector('.rmdp-day-picker');

    const isPickerInBlockDisplay = (pickerElement) => (
      pickerElement
      && pickerElement.style
      && pickerElement.style.display === 'block'
      && isVisible(pickerElement)
    );

    let activePicker = dayPicker;
    if (isPickerInBlockDisplay(yearPicker)) {
      activePicker = yearPicker;
    } else if (isPickerInBlockDisplay(monthPicker)) {
      activePicker = monthPicker;
    }

    if (!activePicker || !isVisible(activePicker)) {
      return null;
    }

    return activePicker.querySelector(
      '.rmdp-day.rmdp-selected, .rmdp-day.rmdp-today, .rmdp-day:not(.rmdp-day-hidden), .rmdp-month, .rmdp-year',
    );
  };

  const getFirstPickerGridElement = (overlayElement) => {
    const yearPicker = overlayElement.querySelector('.rmdp-year-picker');
    const monthPicker = overlayElement.querySelector('.rmdp-month-picker');
    const dayPicker = overlayElement.querySelector('.rmdp-day-picker');

    const isPickerInBlockDisplay = (pickerElement) => (
      pickerElement
      && pickerElement.style
      && pickerElement.style.display === 'block'
      && isVisible(pickerElement)
    );

    let activePicker = dayPicker;
    if (isPickerInBlockDisplay(yearPicker)) {
      activePicker = yearPicker;
    } else if (isPickerInBlockDisplay(monthPicker)) {
      activePicker = monthPicker;
    }

    if (!activePicker || !isVisible(activePicker)) {
      return null;
    }

    return activePicker.querySelector(
      '.rmdp-day:not(.rmdp-day-hidden), .rmdp-month, .rmdp-year',
    );
  };

  const focusFirstOverlayElement = () => {
    const overlayElement = getActiveOverlay();
    if (!overlayElement) {
      return false;
    }

    const selectedOrActiveDay = overlayElement.querySelector(
      '.rmdp-day.rmdp-selected, .rmdp-day.rmdp-today, .rmdp-day:not(.rmdp-day-hidden)',
    );

    if (selectedOrActiveDay && isVisible(selectedOrActiveDay)) {
      selectedOrActiveDay.focus();
      return true;
    }

    const focusableElements = getFocusableElements(overlayElement);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
      return true;
    }

    if (!overlayElement.hasAttribute('tabindex')) {
      overlayElement.setAttribute('tabindex', '-1');
    }
    overlayElement.focus();
    return true;
  };

  const handleDatePickerOpen = () => {
    clearOverlayFocusTrap();

    let focusRetryTimeout = null;

    const handleTabKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeCalendarOverlay();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const overlayElement = getActiveOverlay();
      if (!overlayElement) {
        return;
      }

      if (
        !event.shiftKey
        && document.activeElement
        && document.activeElement.classList
        && document.activeElement.classList.contains('rmdp-right')
      ) {
        const nextPickerElement = getFirstVisiblePickerElement(overlayElement);
        if (nextPickerElement && isVisible(nextPickerElement)) {
          event.preventDefault();
          nextPickerElement.focus();
          return;
        }
      }

      if (event.shiftKey) {
        const firstPickerItem = getFirstPickerGridElement(overlayElement);
        const rightChevron = overlayElement.querySelector('.rmdp-right');
        if (
          firstPickerItem
          && rightChevron
          && document.activeElement === firstPickerItem
          && isVisible(rightChevron)
        ) {
          event.preventDefault();
          rightChevron.focus();
          return;
        }
      }

      const focusableElements = getFocusableElements(overlayElement);
      event.preventDefault();

      if (focusableElements.length === 0) {
        overlayElement.focus();
        return;
      }

      const currentIndex = focusableElements.indexOf(document.activeElement);
      let nextIndex = 0;

      if (currentIndex !== -1) {
        if (event.shiftKey) {
          nextIndex = currentIndex === 0 ? focusableElements.length - 1 : currentIndex - 1;
        } else {
          nextIndex = currentIndex === focusableElements.length - 1 ? 0 : currentIndex + 1;
        }
      } else if (event.shiftKey) {
        nextIndex = focusableElements.length - 1;
      }

      focusableElements[nextIndex].focus();
    };

    const handleFocusIn = (event) => {
      const overlayElement = getActiveOverlay();
      if (!overlayElement) {
        return;
      }

      if (!overlayElement.contains(event.target)) {
        const focusableElements = getFocusableElements(overlayElement);
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        } else {
          overlayElement.focus();
        }
      }
    };

    const attemptFocus = (retriesLeft = 12) => {
      if (focusFirstOverlayElement() || retriesLeft <= 0) {
        return;
      }
      focusRetryTimeout = setTimeout(() => {
        attemptFocus(retriesLeft - 1);
      }, 30);
    };

    document.addEventListener('keydown', handleTabKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);
    attemptFocus();

    overlayFocusTrapCleanupRef.current = () => {
      if (focusRetryTimeout) {
        clearTimeout(focusRetryTimeout);
      }
      document.removeEventListener('keydown', handleTabKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
    };
  };

  // Add tabindex and keyboard navigation to date picker elements for accessibility
  useEffect(() => {
    const addAccessibilityToDateElements = () => {
      const selectors = ['.rmdp-day:not(.rmdp-day-hidden)', '.rmdp-left', '.rmdp-right', '.rmdp-header-values span'];
      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((element) => {
          element.setAttribute('tabindex', '0');
          // Add keydown event listener if not already added
          if (!element.dataset.keydownAdded) {
            element.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                // Special handling for header spans
                if (element.parentElement && element.parentElement.classList.contains('rmdp-header-values')) {
                  element.click();
                  setTimeout(() => {
                    const spans = element.parentElement.querySelectorAll('span');
                    const spanIndex = Array.from(spans).indexOf(element);
                    if (spanIndex === 0) {
                      // Focus first day in month picker
                      const firstDay = document.querySelector('.rmdp-month-picker .rmdp-day');
                      if (firstDay) firstDay.focus();
                    } else if (spanIndex === 1) {
                      // Focus year picker
                      const firstDay = document.querySelector('.rmdp-year-picker .rmdp-day');
                      if (firstDay) firstDay.focus();
                    }
                  }, 100);
                } else {
                  element.click();
                }
              }
            });
            // eslint-disable-next-line no-param-reassign
            element.dataset.keydownAdded = 'true';
          }
        });
      });
    };

    // Run initially and set up a mutation observer for dynamic content
    const observer = new MutationObserver(() => {
      addAccessibilityToDateElements();
    });

    const targetNode = document.querySelector('.date-picker-component');
    if (targetNode) {
      observer.observe(targetNode, { childList: true, subtree: true });
    }

    addAccessibilityToDateElements();

    return () => {
      observer.disconnect();
    };
  }, [mode, selectedDates]);

  useEffect(() => (
    () => {
      clearOverlayFocusTrap();
    }
  ), [clearOverlayFocusTrap]);

  const resetDaysSelection = () => {
    onSelectedDatesChange({
      isDateRange: mode === DATE_SELECTOR_MODE.DATE_RANGE,
      dates: [],
    });
  };

  const handleChange = (e) => {
    setMode(e.target.value);
    resetDaysSelection();
  };

  const plugins = [];
  if (showDatePanel) {
    plugins.push(<DatePanel />);
  }
  return (
    <div className={`multiple-date-selector ${formMode ? 'form-mode' : ''} ${className}`}>
      <div className='date-picker-component-container' data-test='multiple-date-selector'>
        <DatePicker
          multiple={mode === DATE_SELECTOR_MODE.MULTIPLE_DATES}
          range={mode === DATE_SELECTOR_MODE.DATE_RANGE}
          animation
          value={selectedDates.dates}
          format='MM/DD/YYYY'
          minDate={minDate}
          maxDate={maxDate}
          // type={`${showText ? 'button' : 'icon'}`}
          containerClassName={`date-picker-component ${highlightToday ? '' : 'no-highlight'} ${showText ? 'with-text' : ''}`}
          ref={datePickerRef}
          onOpen={handleDatePickerOpen}
          onClose={() => {
            clearOverlayFocusTrap();
            onClose();
          }}
          onChange={(dates) => {
            const sortedDate = (mode === DATE_SELECTOR_MODE.MULTIPLE_DATES ? dates.sort() : dates);
            const formattedDates = sortedDate.map((d) => d.format('MM/DD/YYYY'));
            onSelectedDatesChange({
              isDateRange: mode === DATE_SELECTOR_MODE.DATE_RANGE,
              dates: formattedDates,
            });
          }}
          plugins={plugins}
          renderInput={(params) => <TextField {...params} variant='standard' />}
          render={formMode ? undefined : (_value, openCalendar) => getMultipleDateSelectorPlaceHolder(mode, showText, openCalendar)}
        >
          {selectorMode === null && (
            <RadioGroup
              className='multiple-date-selector-radio-group'
              value={mode}
              onChange={handleChange}
            >
              <FormControlLabel
                value={DATE_SELECTOR_MODE.DATE_RANGE}
                control={<Radio />}
                label='Select Date Range'
                data-test='select-date-range'
              />
              <FormControlLabel
                value={DATE_SELECTOR_MODE.MULTIPLE_DATES}
                control={<Radio />}
                label='Select Days'
                data-test='select-days'
              />
            </RadioGroup>
          )}
        </DatePicker>
        {(!noApplyButton || !noClearButton) && (
          <Divider className='divider' orientation='vertical' />
        )}
        {!noApplyButton && (
          <IconButton
            aria-label='Apply'
            size='small'
            disabled={!selectedDates || !selectedDates.dates || selectedDates.dates.length === 0}
            className='btn-green-background apply-date-button'
            onClick={onApply}
            data-test='apply-button'
            tabIndex={!selectedDates || !selectedDates.dates || selectedDates.dates.length === 0 ? -1 : 0}
          >
            <CheckIcon />
          </IconButton>
        )}

        {!noClearButton && (
          <IconButton
            aria-label='Clear Dates'
            size='small'
            disabled={!selectedDates || !selectedDates.dates || selectedDates.dates.length === 0}
            className='btn-danger'
            onClick={resetDaysSelection}
            data-test='clear-dates-button'
            tabIndex={!selectedDates || !selectedDates.dates || selectedDates.dates.length === 0 ? -1 : 0}
          >
            <ClearIcon />
          </IconButton>
        )}
      </div>
    </div>
  );
}
